import { FacilityDetailsResponse } from "@features/ax/assetRegistry/facilities/types";
import { getConditionLabel } from "@features/ax/utils";
import { logEvent } from "@shared/analytics/analytics";
import { GenericTable } from "@shared/components/GenericTable";
import { HoverRevealText } from "@shared/components/HoverRevealText";
import { PageHeader } from "@shared/components/PageHeader";
import { useToast } from "@shared/hooks/use-toast";
import { TableAction, TableColumn } from "@shared/types/table";
import { Badge } from "@shared/ui/badge";
import { Button } from "@shared/ui/button";
import { Card, CardContent } from "@shared/ui/card";
import { PAGE_SIZE } from "@shared/utils/constants";
import { Plus, Settings } from "lucide-react";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useSystemsList } from "../../../../hooks";
import { SystemFilters, SystemItem } from "../../../../types";
import { SystemSearchModal } from "./systemSearchModal";
import { SystemSuggestionsModal } from "./systemSuggestedModal";

interface SystemsTabProps {
  facility: FacilityDetailsResponse;
}

const getRiskColor = (score: number) => {
  if (score >= 70) return "bg-red-100 text-red-800";
  if (score >= 40) return "bg-yellow-100 text-yellow-800";
  return "bg-green-100 text-green-800";
};

const SystemsTab: React.FC<SystemsTabProps> = ({ facility }) => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [currentPage, setCurrentPage] = useState(1);
  const [showSuggestionsModal, setShowSuggestionsModal] = useState(false);
  const [showSearchModal, setShowSearchModal] = useState(false);
  const { toast } = useToast();

  const itemsPerPage = PAGE_SIZE;
  useEffect(() => {
    logEvent("systems_tab_viewed");
  }, []);
  // --- Parse URL params into filters ---
  const filters = useMemo<SystemFilters>(
    () => ({
      page: currentPage,
      limit: itemsPerPage,
      facilityId: facility.id,
      search: searchParams.get("search") || "",
      location: searchParams.get("location")?.split(",") || [],
      conditionScore: searchParams.get("condition_score") || "all",
      riskScore: searchParams.get("risk_score") || "all",
    }),
    [searchParams, facility.id, currentPage, itemsPerPage]
  );

  const { data } = useSystemsList(filters);

  const totalItems = data?.count || 0;
  const totalPages = Math.ceil(totalItems / PAGE_SIZE);
  const startIndex = (currentPage - 1) * PAGE_SIZE;

  const pagination = useMemo(
    () => ({
      currentPage,
      totalPages,
      totalItems,
      itemsPerPage: PAGE_SIZE,
      startItem: startIndex + 1,
      endItem: Math.min(currentPage * PAGE_SIZE, totalItems),
    }),
    [currentPage, totalPages, totalItems, startIndex]
  );

  // --- Search & Pagination ---
  const handleSearch = useCallback(
    (searchTerm: string, paramName: string) => {
      const newParams = new URLSearchParams(searchParams);
      if (searchTerm.trim()) newParams.set(paramName, searchTerm.trim());
      else newParams.delete(paramName);
      newParams.set("page", "1");
      setCurrentPage(1);
      setSearchParams(newParams, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  const handlePageChange = useCallback(
    (newPage: number) => {
      const newParams = new URLSearchParams(searchParams);
      newParams.set("page", newPage.toString());
      setCurrentPage(newPage);
      setSearchParams(newParams, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  // --- Table Columns & Actions ---
  const systemsColumns: TableColumn<SystemItem>[] = [
    {
      key: "systemNo",
      header: "System ID",
      type: "text",
      sortable: true,
      width: "w-32",
      headerTooltip: "The unique identifier for the system",
    },
    {
      key: "systemName",
      header: "Name",
      type: "text",
      sortable: true,
      width: "w-48",
      headerTooltip:
        "The common name of the system (e.g., Water Treatment Plant A).",
    },
    {
      key: "location",
      header: "Location",
      type: "custom",
      render: (value) => <HoverRevealText text={value} />,
      headerTooltip: "The physical location of the system",
    },
    {
      key: "assetCount",
      header: "Assets",
      type: "custom",
      headerTooltip:
        "The total count of individual assets that belong to this system.",
    },
    {
      key: "conditionScore",
      header: "Condition Score",
      type: "custom",
      render: (value: number) => (
        <div className="flex items-center gap-2">
          <span className="font-medium">{value}</span>
          <Badge variant="secondary" className={getConditionLabel(value).color}>
            {getConditionLabel(value).label}
          </Badge>
        </div>
      ),
      headerTooltip:
        "The aggregated health score of the system, calculated from its child assets.",
    },
    {
      key: "riskScore",
      header: "Risk Score",
      type: "custom",
      render: (value: number, row: any) => (
        <div className="flex items-center gap-2">
          <span className="font-medium">{value}</span>
          <Badge variant="secondary" className={getRiskColor(value)}>
            {row.riskStatus ?? "NA"}
          </Badge>
        </div>
      ),
      headerTooltip: "The aggregated risk score of the system. ",
    },
  ];

  const systemsActions: TableAction[] = [
    {
      label: "View Details",
      onClick: () => {
        navigate(`/ax/asset-registry/systems/view/${facility.id}`);
      },
      icon: "Eye",
      variant: "outline",
    },
  ];

  // --- Modals ---
  const handleAddSystem = useCallback(() => setShowSuggestionsModal(true), []);
  const handleConnectSuggestions = useCallback(
    (systemIds: string[]) => {
      setShowSuggestionsModal(false);
      toast({
        title: "Systems Connected",
        description: `Successfully connected ${systemIds.length} system(s) to ${facility.facilityName}`,
      });
    },
    [facility.facilityName, toast]
  );
  const handleSkipToSearch = useCallback(() => {
    setShowSuggestionsModal(false);
    setShowSearchModal(true);
  }, []);
  const handleConnectFromSearch = useCallback(
    (systemIds: string[]) => {
      setShowSearchModal(false);
      toast({
        title: "Systems Connected",
        description: `Successfully connected ${systemIds.length} system(s) to ${facility.facilityName}`,
      });
    },
    [facility.facilityName, toast]
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
            <Settings className="h-5 w-5 text-blue-600" />
          </div>
          <PageHeader
            title="Systems Management"
            subtitle={`Monitor and manage all systems within ${facility.facilityName}`}
          />
        </div>
        <Button
          onClick={() => {
            navigate("/ax/asset-registry/systems/create");
          }}
          className="bg-blue-600 hover:bg-blue-700 flex items-center gap-2"
        >
          <Plus className="h-4 w-4" /> Add System
        </Button>
      </div>

      {/* Table */}
      <Card>
        <CardContent>
          <GenericTable
            data={data?.results || []}
            columns={systemsColumns}
            rowKey="id"
            actions={systemsActions}
            actionsType="dropdown"
            searchPlaceholder="Search systems by ID, name..."
            searchParamName="search"
            searchDebounceMs={300}
            onSearch={handleSearch}
            pagination={pagination}
            onPageChange={handlePageChange}
            emptyMessage="No systems found for this facility"
            emptyIcon={<Settings className="h-8 w-8 text-gray-400" />}
          />
        </CardContent>
      </Card>

      {/* Modals */}
      <SystemSuggestionsModal
        isOpen={showSuggestionsModal}
        onClose={() => setShowSuggestionsModal(false)}
        facilityName={facility.facilityName}
        facilityType={facility.facilityTypeDisplay}
        onConnectSuggestions={handleConnectSuggestions}
        onSkipToSearch={handleSkipToSearch}
      />
      <SystemSearchModal
        isOpen={showSearchModal}
        onClose={() => setShowSearchModal(false)}
        facilityName={facility.facilityName}
        onConnectSystems={handleConnectFromSearch}
      />
    </div>
  );
};

export default SystemsTab;
